import type { AssessmentScoreKey, GapStatus } from '@/services/competency.service';

export const COMPETENCY_BASE = '/grc/competency';

/** NTES-QSF-HR-03 month headers (June/July spelled out). */
export const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const COMPETENCY_LEVELS = ['Basic', 'Intermediate', 'Advanced', 'Expert'];

export const REQUIREMENT_TYPES = ['Education', 'Experience', 'Skill', 'Competency', 'Certification', 'Training'] as const;

export const COURSE_CATEGORIES = ['QMS', 'HSE induction', 'Company orientation', 'On-job', 'Customer-specified', 'Change-driven', 'Other'];

export const COURSE_FREQUENCIES = ['One-time', 'Annual', 'Every N months'];

export const PLAN_SOURCES = ['TNA', 'Induction', 'NCR', 'CAPA', 'MOC', 'Customer', 'Assessment', 'Effectiveness', 'Refresher'];

export const ASSESSMENT_TYPES = ['Probationary', 'Probation re-assessment', 'Yearly', 'Role change', 'Promotion', 'Post-training', 'Other'];
export const PROBATION_TYPES = ['Probationary', 'Probation re-assessment'];

/** NTES-QSF-HR-02 display labels (stored values unchanged). */
export const ASSESSMENT_TYPE_LABEL: Record<string, string> = {
  Probationary: 'New employee / Probationary',
  'Probation re-assessment': 'New employee / Probationary (re-assessment)',
  Yearly: 'Regular / Yearly',
  'Role change': 'Others — Role change',
  Promotion: 'Others — Promotion',
  'Post-training': 'Others — Post-training',
  Other: 'Others',
};

export function assessmentTypeLabel(type?: string | null, other?: string | null): string {
  const t = String(type || '').trim();
  if (!t) return '-';
  if (t === 'Other' && other) return `Others — ${String(other).trim()}`;
  return ASSESSMENT_TYPE_LABEL[t] || t;
}

export const ASSESSMENT_PARAMETERS: Array<{ key: AssessmentScoreKey; label: string }> = [
  { key: 'score_job_knowledge', label: 'Job Knowledge' },
  { key: 'score_attendance', label: 'Attendance and punctuality' },
  { key: 'score_teamwork', label: 'Team spirit and cooperation' },
  { key: 'score_communication', label: 'Relationships with peers and communication' },
  { key: 'score_productivity', label: 'Productivity' },
  { key: 'score_suggestions', label: 'Giving suggestions and feedback' },
  { key: 'score_compliance', label: 'Compliance to company policies' },
  { key: 'score_attitude', label: 'Attitude, Interest for learning and improvement' },
];
export const MAX_PARAMETER_SCORE = 5;

export const GRADE_LABEL: Record<string, string> = {
  A: 'Exceptional',
  B: 'Exceed Expectation',
  C: 'Meet Expectation',
  D: 'Below Average',
  E: 'Poor Performance',
};

export const GRADE_CRITERIA = [
  'A – Exceptional (35-40): Performance consistently exceeds requirements; individual demonstrates initiative and desire to take on additional responsibility and train other employees.',
  'B – Exceed Expectation (30-35, excluding 35): Performance consistently meets and frequently exceeds requirements; individual continues searching for more job knowledge and may seek guidance in some areas.',
  'C – Meet Expectation (25-30, excluding 30): Performance consistently meets requirements; individual may need assistance to complete difficult tasks.',
  'D – Below Average (20-25, excluding 25): Performance generally meets but is occasionally below requirements; individual does not attempt to acquire new skills or knowledge to improve performance.',
  'E – Poor Performance (Below 20): Performance consistently does not meet job requirements; individual has no appreciation for improving his or her performance and requires immediate action if the individual is to remain employed.',
];

/** Mirrors the server: A 35-40, B 30-34, C 25-29, D 20-24, E below 20. */
export function gradeForTotal(total: number): string {
  if (total >= 35) return 'A';
  if (total >= 30) return 'B';
  if (total >= 25) return 'C';
  if (total >= 20) return 'D';
  return 'E';
}

export const GRADE_COLOR: Record<string, string> = { A: 'green', B: 'cyan', C: 'blue', D: 'orange', E: 'red' };

export function recommendationsFor(type: string): string[] {
  return PROBATION_TYPES.includes(type)
    ? ['Confirm', 'Extend probation', 'Additional training', 'Terminate']
    : ['Continue', 'Additional training', 'Terminate'];
}

/** NTES-QSF-HR-05 evaluation methods. */
export const EFFECTIVENESS_METHODS: Array<{ value: string; label: string }> = [
  { value: 'A', label: 'A. Interview' },
  { value: 'B', label: 'B. On Job Assessment' },
  { value: 'C', label: 'C. Observation Checklists' },
  { value: 'D', label: 'D. Written Tests' },
];

/** NTES-QSF-HR-05 score legend. */
export const EFFECTIVENESS_SCORES: Array<{ value: number; label: string }> = [
  { value: 3, label: '3. Very Good (Can Train other and work Independently)' },
  { value: 2, label: '2. Good (Can work Independently)' },
  { value: 1, label: '1. Satisfactory (allowed to work under supervision till re-training)' },
  { value: 0, label: '0. Poor (Re-Training needed)' },
];

export const GAP_COLOR: Record<GapStatus, string> = { Met: 'green', Gap: 'red', Expiring: 'gold', Expired: 'volcano' };

export const STATUS_COLOR: Record<string, string> = {
  Draft: 'default',
  Submitted: 'processing',
  Closed: 'success',
  Planned: 'blue',
  Completed: 'green',
  Cancelled: 'default',
  Open: 'gold',
  Acknowledged: 'green',
  Active: 'green',
  Expired: 'red',
  Present: 'green',
  Absent: 'red',
  Excused: 'default',
};

export const employeeLabel = (e: { employee_number?: string | null; full_name?: string | null }) =>
  [e.employee_number, e.full_name].filter(Boolean).join(' - ');
